import { FC, useContext, useEffect, useMemo } from "react"
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import DashboardTile from "@app/shared/qes-standards/DashboardTile";
import EnterpriseComplianceDashboardTimeFrame from "@app/overviews/enterprise-compliance-dashboard/components/EcdTimeFrame";
import ExceptionsChart from "@app/overviews/enterprise-compliance-dashboard/components/ExceptionsChart";
import { EcdColorsContext } from "@app/overviews/enterprise-compliance-dashboard/contexts/EcdColorsContext";
import { EnterpriseComplianceDashboardContext } from "@app/overviews/enterprise-compliance-dashboard/contexts/EcdContext";
import { EnterpriseComplianceFiltersContext } from "@app/overviews/enterprise-compliance-dashboard/contexts/EcdFiltersContext";
import { tileFlyoutLink } from "@app/overviews/enterprise-compliance-dashboard/flyouts/EcdDrillInConfig";
import EcdExceptionsFlyout from "@app/overviews/enterprise-compliance-dashboard/flyouts/EcdExceptionsFlyout";
import { EcdTilesEnum, TimeFrameOptions } from "@app/overviews/enterprise-compliance-dashboard/interfaces";
import { filterToTimeFrame } from "@app/overviews/enterprise-compliance-dashboard/utility";
import { QesIcons } from "@app/shared/icons/qesIcons";

const ExceptionsTile: FC = () => {
    const filtersContext = useContext(EnterpriseComplianceFiltersContext);
    const dashboardContext = useContext(EnterpriseComplianceDashboardContext);
    const colorsContext = useContext(EcdColorsContext);
    const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
    const title = "Exceptions/Justifications";

    const slicedExceptionsData = useMemo(() => {
        return filterToTimeFrame(dashboardContext.exceptionsData, filtersContext.exceptionsTimeFrame);
    }, [dashboardContext.exceptionsData, filtersContext.exceptionsTimeFrame]);

    useEffect(() => {
        dashboardContext.updateIsTileLoadingMap(EcdTilesEnum.Exceptions, true);
    }, []);

    useEffect(() => {
        if (slicedExceptionsData?.length > 0) {
            dashboardContext.updateIsTileLoadingMap(EcdTilesEnum.Exceptions, false);
        }
    }, [slicedExceptionsData]);

    useEffect(() => {
        if (!!filtersContext.apiReadyFilters) {
            dashboardContext.updateIsTileLoadingMap(EcdTilesEnum.Exceptions, true);
        }
    }, [filtersContext.apiReadyFilters]);

    const flyoutLinkTitle = useMemo(() => {
        if (!featureFlags.has(Features.ENABLE_ECD_DRILLINS) || dashboardContext.isDataEmptyMap.get(EcdTilesEnum.Exceptions)) {
            return title;
        }

        return tileFlyoutLink(
            <EcdExceptionsFlyout />,
            "Exceptions Details",
            filtersContext.isDashboardLoading || dashboardContext.isTileLoadingMap.get(EcdTilesEnum.Exceptions),
            "exceptions-tile__title-flyout",
            title,
        );
    }, [
        filtersContext.isDashboardLoading,
        dashboardContext.exceptionsData,
        featureFlags.has(Features.ENABLE_ECD_DRILLINS),
        dashboardContext.isDataEmptyMap.get(EcdTilesEnum.Exceptions),
        dashboardContext.isTileLoadingMap.get(EcdTilesEnum.Exceptions)
    ]);

    return (
        <DashboardTile
            headerElement={
                <EnterpriseComplianceDashboardTimeFrame
                    disabled={dashboardContext.isTileLoadingMap.get(EcdTilesEnum.Exceptions)}
                    onChange={(value: string) => filtersContext.setExceptionsTimeFrame(value as TimeFrameOptions)}
                    selectedKey={filtersContext.exceptionsTimeFrame}
                    tileKey="exceptions-tile"
                />
            }
            loading={filtersContext.isDashboardLoading || dashboardContext.isTileLoadingMap.get(EcdTilesEnum.Exceptions)}
            title={
                <>
                    <span>{flyoutLinkTitle}</span>
                    <TooltipWrapper text="Historical Exception statuses reflect the status as of the subsequent project run date." containerClassName="ms-2">
                        <QesIcon iconName={QesIcons.InfoCircle} color="#056edf" width="16" height="16" className="mb-1" description="Informational tooltip trigger" />
                    </TooltipWrapper>
                </>
            }
            widthClass="col-md-12 col-xl-7"
        >
            <ExceptionsChart
                data={slicedExceptionsData}
                timeFrame={filtersContext.exceptionsTimeFrame}
                barHoverBackground={colorsContext.barHoverBackground}
                exceptionsArray={colorsContext.exceptionsArray}
                isExport={false}
            />
        </DashboardTile>
    )
};

export default ExceptionsTile;